import {
  isSignedPermissionSnapshotGrant,
  PERMISSION_SNAPSHOT_KEY_PREFIX,
  PERMISSION_SNAPSHOT_MAX_OVERLAYS,
  PERMISSION_SNAPSHOT_PARTITION_COUNT,
  type PermissionSnapshotBundle as PermissionSnapshotContractBundle,
  permissionSnapshotPartitionIndex,
  type PermissionSnapshotPayload as PermissionSnapshotContractPayload,
  type PermissionSnapshotResponse as PermissionSnapshotContractResponse,
  type SignedPermissionSnapshotGrant,
  type StoredPermissionPartition,
  type StoredPermissionPointer,
  type StoredSignedEnvelope,
  type WorkspacePermissionGraphAdvancedPayload,
  type WorkspacePermissionsChangedPayload,
} from '@streamlain/permission-snapshot-contract';
import type { LocalAuthorityEnvelope } from '@streamlain/view-config';
import { LiveData, Store } from '@toeverything/infra';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import type { WorkspaceServerService } from '../../cloud';
import {
  deletePermissionSnapshotVault,
  getLocalAuthorityClientInstallId,
  readPermissionSnapshotVault,
  stableLocalAuthorityScopeHash,
  verifySignedLocalAuthorityEnvelope,
  writePermissionSnapshotVault,
} from '../../local-authority';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import { resolveWorkspaceLocalAuthorityAccountId } from '../../workspace/utils/permission-cache-identity';
import {
  isValidOfflineAccessTtlMs,
  OFFLINE_ACCESS_DEFAULT_TTL_MS,
  offlineAccessRenewalDelayMs,
} from '../offline-access-policy';
import type { PermissionResourceReader } from './permission-resource-reader';

export interface PermissionSnapshotGrant {
  resourceId: string;
  resourceType: 'doc' | 'folder';
  permissions: Record<string, boolean>;
  role?: string;
  permissionSource?:
    | 'canonical-doc-permission-service'
    | 'canonical-folder-permission-service'
    | 'pending-local-owner';
  localMutationId?: string;
  trashed: boolean;
  updatedAt: string | null;
}

/** Signed payload as verified: the contract payload plus the signing times. */
export type PermissionSnapshotPayload = PermissionSnapshotContractPayload & {
  issuedAt: string;
  expiresAt: string;
};

type PermissionSnapshotEnvelope =
  LocalAuthorityEnvelope<PermissionSnapshotContractPayload>;

export type PermissionSnapshotResponse =
  PermissionSnapshotContractResponse<PermissionSnapshotEnvelope>;

export type PermissionSnapshotBundle =
  PermissionSnapshotContractBundle<PermissionSnapshotEnvelope>;

/**
 * One chunk of a partition's complete grant set. The chunks replace the
 * partition's base only once the last one arrived.
 */
export interface PermissionSnapshotBaseTarget {
  kind: 'base';
  partitionIndex: number;
  chunkIndex: number;
  chunkCount: number;
}

/** A `workspace:permissions-changed` event as the sync engine delivers it. */
export type PermissionChangeEvent = Omit<
  WorkspacePermissionsChangedPayload,
  'changeType'
> & { changeType: string };

export interface PermissionSnapshotResource {
  id: string;
  type: 'doc' | 'folder';
}

export interface PermissionSnapshotScope {
  tenantId?: string | null;
  workspaceId: string;
  userId: string;
  serverId: string;
  serverBaseUrlHash: string;
  clientInstallId: string;
  visibilityMode: 'user_effective';
}

/** Vault read results that another read in the same session cannot fix. */
const PERMANENT_VAULT_FAILURES: ReadonlySet<string> = new Set([
  'corrupt',
  'unsupported',
]);

export class PermissionSnapshotStore extends Store {
  /**
   * Grants by resource key. Only this store writes it; readers go through the
   * permission getters and follow `grantsVersion$`.
   */
  private readonly grants = new Map<string, PermissionSnapshotGrant>();
  /** Goes up once per change of the grants (one answer, one load, one expiry). */
  readonly grantsVersion$ = new LiveData(0);
  readonly vaultStatus$ = new LiveData<LocalPermissionVaultStatus>('ready');
  /**
   * Resources whose offline grant ran out without renewal. They stay locked
   * until a fresh signed grant arrives; the UI explains how to renew.
   */
  readonly expiredGrants$ = new LiveData<ReadonlyMap<string, ExpiredGrant>>(
    new Map()
  );

  private offlineAccessTtlMs = OFFLINE_ACCESS_DEFAULT_TTL_MS;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Highest counter value this store has accounted for: from signed
   * snapshots, permission events and list responses. Persisted as the
   * pointer's confirmedPermissionGraphVersion.
   */
  private verifiedPermissionGraphVersion = 0;
  private verifiedTenantId: string | null = null;
  /**
   * Resource key → counter value of the event that marked it stale; mirrors
   * the vault pointer so marks survive a reload.
   */
  private readonly staleKeys = new Map<string, number>();
  /** Grants signed below this counter value count as stale. */
  private staleBelowVersion = 0;
  private lastReportedStaleSnapshotKey: string | null = null;
  private hydration: Promise<void> | null = null;
  /** Pointer key of the scope whose vault this store already read. */
  private hydratedScopeKey: string | null = null;
  private readonly grantAuthority = new Map<string, GrantAuthority>();
  private readonly pendingLocalOwners = new Map<string, PendingLocalOwner>();
  private onlineSnapshotWrite: Promise<void> = Promise.resolve();
  private pendingLocalOwnerWrite: Promise<void> = Promise.resolve();
  /** Base chunks of a partition refresh that has not delivered all chunks. */
  private readonly pendingBaseChunks = new Map<number, PendingBaseChunks>();
  /** Partitions whose overlays overflowed since their base was replaced. */
  private readonly partitionsNeedingBaseRefresh = new Set<number>();

  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly workspaceService: WorkspaceService,
    private readonly globalState?: GlobalState,
    private readonly resourceReader?: PermissionResourceReader
  ) {
    super();
    this.disposables.push(() => this.clearExpiryTimer());
  }

  getPermissionGraphVersion(): number {
    return this.verifiedPermissionGraphVersion;
  }

  getVerifiedTenantId(): string | null {
    return this.verifiedTenantId;
  }

  getDocPermissions(docId: string): Record<string, boolean> | null {
    return this.getActiveGrant('doc', docId)?.permissions ?? null;
  }

  getFolderPermissions(folderId: string): Record<string, boolean> | null {
    return this.getActiveGrant('folder', folderId)?.permissions ?? null;
  }

  getPermissionState(
    resourceType: 'doc' | 'folder',
    resourceId: string
  ): LocalPermissionGrantState {
    const key = grantKey(resourceType, resourceId);
    const grant = this.grants.get(key);
    const authority = this.grantAuthority.get(key);
    const scope = this.currentScope();
    if (!grant || !authority) {
      if (this.expiredGrants$.value.has(key)) return 'expired';
      const vaultStatus = this.vaultStatus$.value;
      return vaultStatus === 'locked'
        ? 'locked'
        : vaultStatus === 'corrupt'
          ? 'invalid'
          : vaultStatus === 'ready'
            ? 'unknown'
            : 'degraded';
    }
    if (authority.expiresAt <= Date.now()) return 'expired';
    if (authority.source === 'pending-local-owner') return 'pending-local';
    if (
      !scope ||
      authority.scopeFingerprint !== permissionScopeFingerprint(scope)
    ) {
      return 'invalid';
    }
    if (this.isMarkedStale(key, authority.permissionGraphVersion)) {
      return 'stale';
    }
    return 'valid';
  }

  /**
   * A counter value from a list response or other contact path. A value past
   * the confirmed one means events were missed: every grant goes stale.
   */
  observePermissionGraphVersion(version: number): void {
    if (
      !Number.isFinite(version) ||
      version <= this.verifiedPermissionGraphVersion
    ) {
      return;
    }
    this.markAllStale(version);
    this.persistStaleMarks();
  }

  /**
   * Marks the grants a `workspace:permissions-changed` event names as stale.
   * Fails closed to all grants when the event has no counter value, asks for
   * a full refresh, changes a workspace membership, skips a counter value or
   * names no page or folder. A named folder marks itself, its subfolders
   * and the cached pages inside them; an uncached folder marks every grant.
   * A created page or folder only adds access, so it marks nothing and only
   * asks for its own grant.
   */
  applyPermissionEvent(event: PermissionChangeEvent): {
    staleDocIds: string[];
    full: boolean;
  } {
    const version = event.permissionGraphVersion;
    const confirmed = this.verifiedPermissionGraphVersion;
    const docIds = [
      ...new Set([
        ...(event.affectedDocIds ?? []),
        ...(event.docPlacementChanges ?? []).map(change => change.docId),
      ]),
    ];
    const namedFolderIds = [
      ...new Set([
        ...(event.affectedFolderIds ?? []),
        ...(event.revokedFolderIds ?? []),
      ]),
    ];
    const counterGap = version !== undefined && version > confirmed + 1;
    if (
      !counterGap &&
      (event.changeType === 'doc_created' ||
        event.changeType === 'folder_created')
    ) {
      if (version !== undefined) this.confirmPermissionGraphVersion(version);
      this.persistStaleMarks();
      return { staleDocIds: docIds, full: false };
    }
    if (
      version === undefined ||
      counterGap ||
      event.refreshMode === 'full' ||
      // A revocation filters what this user lost out of the event, and a
      // revoked folder's pages may be unknown here: no grant stays trusted.
      event.changeType.endsWith('_revoked') ||
      (event.revokedFolderIds?.length ?? 0) > 0 ||
      event.changeType.startsWith('workspace_member_') ||
      (docIds.length === 0 && namedFolderIds.length === 0)
    ) {
      return this.markAllStaleForEvent(version ?? confirmed + 1);
    }
    const folderIds =
      namedFolderIds.length === 0
        ? []
        : this.resourceReader?.getFolderSubtreeIds(namedFolderIds);
    if (!folderIds) return this.markAllStaleForEvent(version);
    if (folderIds.length > 0) {
      docIds.push(
        ...this.resourceReader!.getDocIdsInFolders(new Set(folderIds)).filter(
          docId => !docIds.includes(docId)
        )
      );
    }
    for (const key of [
      ...docIds.map(docId => grantKey('doc', docId)),
      ...folderIds.map(folderId => grantKey('folder', folderId)),
    ]) {
      this.staleKeys.set(key, Math.max(version, this.staleKeys.get(key) ?? 0));
    }
    this.confirmPermissionGraphVersion(version);
    this.bumpGrantsVersion();
    this.persistStaleMarks();
    return { staleDocIds: docIds, full: false };
  }

  /**
   * `workspace:permission-graph-advanced` carries only a counter value for a
   * change that does not affect this user. The next value marks nothing; a
   * skipped value means a missed event and marks every grant stale.
   */
  applyGraphAdvanced(event: WorkspacePermissionGraphAdvancedPayload): {
    full: boolean;
  } {
    const version = event.permissionGraphVersion;
    const confirmed = this.verifiedPermissionGraphVersion;
    if (!Number.isFinite(version) || version <= confirmed) {
      return { full: false };
    }
    if (version === confirmed + 1) {
      this.confirmPermissionGraphVersion(version);
      this.persistStaleMarks();
      return { full: false };
    }
    this.markAllStale(version);
    this.persistStaleMarks();
    return { full: true };
  }

  private markAllStaleForEvent(version: number): {
    staleDocIds: string[];
    full: boolean;
  } {
    this.markAllStale(version);
    this.persistStaleMarks();
    return { staleDocIds: [], full: true };
  }

  /**
   * Takes over stale marks stored in the pointer (another tab, an earlier
   * session). New marks are news to this store's watchers.
   */
  private adoptStoredStaleMarks(
    pointer: Pick<
      StoredPermissionPointer,
      'staleKeys' | 'staleBelowVersion'
    > | null
  ): void {
    let adopted = false;
    for (const [staleKey, markedAt] of Object.entries(
      pointer?.staleKeys ?? {}
    )) {
      if (markedAt > (this.staleKeys.get(staleKey) ?? 0)) {
        this.staleKeys.set(staleKey, markedAt);
        adopted = true;
      }
    }
    if ((pointer?.staleBelowVersion ?? 0) > this.staleBelowVersion) {
      this.staleBelowVersion = pointer!.staleBelowVersion;
      adopted = true;
    }
    if (adopted) this.bumpGrantsVersion();
  }

  private markAllStale(version: number): void {
    this.staleBelowVersion = Math.max(this.staleBelowVersion, version);
    this.confirmPermissionGraphVersion(version);
    // Watchers read stale grants as missing; tell them the answer changed.
    this.bumpGrantsVersion();
  }

  private confirmPermissionGraphVersion(version: number): void {
    this.verifiedPermissionGraphVersion = Math.max(
      this.verifiedPermissionGraphVersion,
      version
    );
  }

  /** Writes the stale marks to the pointer right away, in write order. */
  private persistStaleMarks(): void {
    const scope = this.currentScope();
    const tenantId = this.verifiedTenantId;
    if (!scope || !tenantId) return;
    const next = this.onlineSnapshotWrite.then(async () => {
      await this.writePointer(scope, tenantId, [], []);
    });
    this.onlineSnapshotWrite = next.catch(error => {
      console.error('Failed to persist permission stale marks:', error);
    });
  }

  /** Offline-access window of the workspace (last verified snapshot). */
  getOfflineAccessTtlMs(): number {
    return this.offlineAccessTtlMs;
  }

  isGrantExpired(resourceType: 'doc' | 'folder', resourceId: string): boolean {
    return this.expiredGrants$.value.has(grantKey(resourceType, resourceId));
  }

  /** Earliest expiry over grants that currently allow at least one action. */
  getOfflineAccessExpiresAt(now = Date.now()): number | null {
    let earliest: number | null = null;
    for (const [key, authority] of this.grantAuthority) {
      if (authority.expiresAt <= now) continue;
      if (!grantAllowsAnything(this.grants.get(key))) continue;
      if (earliest === null || authority.expiresAt < earliest) {
        earliest = authority.expiresAt;
      }
    }
    return earliest;
  }

  /**
   * Partitions to renew online: a signed grant that allows access reached
   * its renewal point, a signed grant expired unrenewed, or the overlays
   * overflowed. Pending local owners are confirmed through doc sync.
   */
  getPartitionsDueForRenewal(now = Date.now()): number[] {
    const due = new Set(this.partitionsNeedingBaseRefresh);
    for (const [partitionIndex, dueAt] of this.partitionRenewalDueAt()) {
      if (now >= dueAt) due.add(partitionIndex);
    }
    for (const [key, expired] of this.expiredGrants$.value) {
      if (expired.source !== 'signed') continue;
      due.add(
        permissionSnapshotPartitionIndex(
          expired.resourceType,
          key.slice(expired.resourceType.length + 1)
        )
      );
    }
    return [...due].sort((left, right) => left - right);
  }

  /** Next time a partition becomes due for online renewal. */
  getNextPartitionRenewalAt(): number | null {
    let next: number | null = null;
    for (const dueAt of this.partitionRenewalDueAt().values()) {
      if (next === null || dueAt < next) next = dueAt;
    }
    return next;
  }

  /**
   * Earliest renewal point per partition over its signed grants that allow
   * access. Every stored envelope carries at least one current grant, so
   * this covers base and overlays alike.
   */
  private partitionRenewalDueAt(): Map<number, number> {
    const dueAt = new Map<number, number>();
    for (const [key, authority] of this.grantAuthority) {
      const grant = this.grants.get(key);
      if (
        authority.source !== 'signed' ||
        !grant ||
        !grantAllowsAnything(grant)
      ) {
        continue;
      }
      const partitionIndex = permissionSnapshotPartitionIndex(
        grant.resourceType,
        grant.resourceId
      );
      const at = this.renewalDueAt(authority);
      dueAt.set(partitionIndex, Math.min(at, dueAt.get(partitionIndex) ?? at));
    }
    return dueAt;
  }

  /**
   * True when an online envelope changes what this store holds, and so what
   * the vault holds: a grant it does not hold as signed, a higher graph
   * version, other content, or a held grant at its renewal point. While the
   * vault is not ready, memory may hold more than the vault: then every
   * envelope counts.
   */
  private bringsNews({ grants, authority }: GrantMerge, now: number): boolean {
    if (this.vaultStatus$.value !== 'ready') return true;
    return grants.some(grant => {
      const key = grantKey(grant.resourceType, grant.resourceId);
      const held = this.grantAuthority.get(key);
      return (
        held?.source !== 'signed' ||
        authority.permissionGraphVersion > held.permissionGraphVersion ||
        now >= this.renewalDueAt(held) ||
        !sameGrant(this.grants.get(key), grant)
      );
    });
  }

  private renewalDueAt(authority: GrantAuthority): number {
    return (
      authority.issuedAt +
      offlineAccessRenewalDelayMs(authority.expiresAt - authority.issuedAt)
    );
  }

  /**
   * Reads the vault into memory once per scope. Later calls return at once:
   * this store keeps memory and vault in step itself. A read that did not
   * finish (locked vault or partition, error) is tried again on the next call.
   */
  async hydrateLocal(): Promise<void> {
    const scope = this.currentScope();
    if (scope && this.scopePointerKey(scope) === this.hydratedScopeKey) {
      return;
    }
    if (!this.hydration) {
      this.hydration = this.hydrateLocalOnce().finally(() => {
        this.hydration = null;
      });
    }
    await this.hydration;
  }

  /**
   * Verifies and applies signed envelopes, each into its fixed partition.
   * Without a target every envelope becomes an overlay of its partition (list
   * pages, single requests); with a base target the chunks replace the
   * partition's base once the last chunk arrived. Runs serially.
   */
  async applyOnlineSnapshots(
    bundle: PermissionSnapshotBundle,
    target?: PermissionSnapshotBaseTarget
  ): Promise<void> {
    const next = this.onlineSnapshotWrite.then(() =>
      this.applyOnlineSnapshotsSerial(bundle, target)
    );
    this.onlineSnapshotWrite = next.catch(error => {
      console.error('Failed to persist online permission snapshot:', error);
    });
    return next;
  }

  /** Partitions whose base should be fetched again (overlays overflowed). */
  getPartitionsNeedingBaseRefresh(): number[] {
    return [...this.partitionsNeedingBaseRefresh].sort(
      (left, right) => left - right
    );
  }

  /** Resources with a grant stored in the partition (base and overlays). */
  async getStoredPartitionResources(
    partitionIndex: number
  ): Promise<PermissionSnapshotResource[]> {
    await this.hydrateLocal();
    const scope = this.currentScope();
    if (!scope || !this.verifiedTenantId) return [];
    const stored = await readPermissionSnapshotVault<StoredPermissionPartition>(
      this.partitionKey(
        { ...scope, tenantId: this.verifiedTenantId },
        partitionIndex
      )
    );
    if (stored.status !== 'ready' || stored.value?.version !== 3) return [];
    const resources = new Map<string, PermissionSnapshotResource>();
    for (const entry of [...stored.value.base, ...stored.value.overlays]) {
      const payload = (
        entry.envelope as { payload?: Partial<PermissionSnapshotPayload> }
      ).payload;
      for (const grant of payload?.resources ?? []) {
        resources.set(grantKey(grant.resourceType, grant.resourceId), {
          id: grant.resourceId,
          type: grant.resourceType,
        });
      }
    }
    return [...resources.values()];
  }

  private async applyOnlineSnapshotsSerial(
    bundle: PermissionSnapshotBundle,
    target: PermissionSnapshotBaseTarget | undefined
  ): Promise<void> {
    const scope = this.currentScope();
    if (!scope) {
      return;
    }
    const acceptedByPartition = new Map<number, StoredSignedEnvelope[]>();
    const acceptedGrants: AcceptedGrantVersion[] = [];
    const merges: GrantMerge[] = [];
    let tenantId: string | null = null;
    const now = Date.now();
    for (const response of bundle.snapshots) {
      const collected: GrantMerge[] = [];
      const outcome = await this.applySignedResponse(response, scope, {
        origin: target ? 'online-base' : 'online',
        expectedPartitionIndex: target?.partitionIndex,
        collect: collected,
      });
      if (outcome.status !== 'accepted') continue;
      const { payload } = outcome;
      tenantId = payload.tenantId;
      // A base replaces the partition and is always kept. An overlay that
      // repeats what this store holds changes neither memory nor vault.
      if (!target && !collected.some(merge => this.bringsNews(merge, now))) {
        continue;
      }
      merges.push(...collected);
      const partition = acceptedByPartition.get(payload.partitionIndex) ?? [];
      partition.push({ cachedAt: Date.now(), envelope: response.envelope });
      acceptedByPartition.set(payload.partitionIndex, partition);
      for (const grant of payload.resources) {
        acceptedGrants.push({
          key: grantKey(grant.resourceType, grant.resourceId),
          permissionGraphVersion: payload.permissionGraphVersion,
        });
      }
    }
    this.mergeGrantBatches(merges);

    // An empty base (no grant left in the partition) carries no envelope;
    // it is stored under the tenant verified earlier. A chunk whose envelopes
    // were all rejected is no empty base and leaves the stored one alone.
    if (target && bundle.snapshots.length === 0) {
      tenantId = this.verifiedTenantId;
    }
    const scopedStorage = { ...scope, tenantId };
    const statuses: VaultWriteStatus[] = [];
    const writtenPartitions: number[] = [];
    if (target) {
      const chunks = this.collectBaseChunk(
        target,
        acceptedByPartition.get(target.partitionIndex) ?? []
      );
      if (!chunks || !tenantId) return;
      const status = await this.writePartition(
        scopedStorage,
        target.partitionIndex,
        record => {
          record.base = chunks;
          record.overlays = [];
          record.baseRefreshedAt = Date.now();
        }
      );
      this.partitionsNeedingBaseRefresh.delete(target.partitionIndex);
      statuses.push(status);
      writtenPartitions.push(target.partitionIndex);
    } else {
      if (!tenantId) return;
      for (const [partitionIndex, envelopes] of acceptedByPartition) {
        const status = await this.writePartition(
          scopedStorage,
          partitionIndex,
          record => {
            record.overlays.push(...envelopes);
          }
        );
        statuses.push(status);
        writtenPartitions.push(partitionIndex);
      }
    }
    statuses.push(
      await this.writePointer(
        scope,
        tenantId,
        writtenPartitions,
        acceptedGrants
      )
    );
    this.vaultStatus$.next(
      statuses.every(status => status === 'ready')
        ? 'ready'
        : statuses.includes('corrupt')
          ? 'corrupt'
          : 'degraded'
    );
  }

  /**
   * Buffers one base chunk. Returns the complete chunk set when the last
   * chunk arrived with all earlier ones, otherwise null (the stored base stays
   * untouched).
   */
  private collectBaseChunk(
    target: PermissionSnapshotBaseTarget,
    envelopes: StoredSignedEnvelope[]
  ): StoredSignedEnvelope[] | null {
    let pending = this.pendingBaseChunks.get(target.partitionIndex);
    if (
      !pending ||
      target.chunkIndex === 0 ||
      pending.chunkCount !== target.chunkCount
    ) {
      pending = { chunkCount: target.chunkCount, chunks: new Map() };
      this.pendingBaseChunks.set(target.partitionIndex, pending);
    }
    pending.chunks.set(target.chunkIndex, envelopes);
    if (target.chunkIndex !== target.chunkCount - 1) return null;
    this.pendingBaseChunks.delete(target.partitionIndex);
    if (pending.chunks.size !== target.chunkCount) {
      this.reportRejectedSnapshot('base-incomplete', {
        partitionIndex: target.partitionIndex,
        receivedChunks: pending.chunks.size,
        chunkCount: target.chunkCount,
      });
      return null;
    }
    return Array.from(
      { length: target.chunkCount },
      (_, index) => pending.chunks.get(index) ?? []
    ).flat();
  }

  private async writePartition(
    scope: PermissionSnapshotScope,
    partitionIndex: number,
    change: (record: StoredPermissionPartition) => void
  ): Promise<VaultWriteStatus> {
    const key = this.partitionKey(scope, partitionIndex);
    const stored =
      await readPermissionSnapshotVault<StoredPermissionPartition>(key);
    const record: StoredPermissionPartition =
      stored.status === 'ready' && stored.value?.version === 3
        ? stored.value
        : {
            version: 3,
            partitionIndex,
            base: [],
            overlays: [],
            baseRefreshedAt: null,
          };
    change(record);
    removeCoveredEnvelopes(record);
    if (record.overlays.length > PERMISSION_SNAPSHOT_MAX_OVERLAYS) {
      // The oldest overlays move into the base so no grant is lost; the
      // partition then waits for a fresh base.
      record.base.push(
        ...record.overlays.splice(
          0,
          record.overlays.length - PERMISSION_SNAPSHOT_MAX_OVERLAYS
        )
      );
      this.partitionsNeedingBaseRefresh.add(partitionIndex);
    }
    return writePermissionSnapshotVault(key, record);
  }

  private async writePointer(
    scope: PermissionSnapshotScope,
    tenantId: string,
    partitions: number[],
    acceptedGrants: AcceptedGrantVersion[]
  ): Promise<VaultWriteStatus> {
    const key = this.scopePointerKey(scope);
    const stored =
      await readPermissionSnapshotVault<StoredPermissionPointer>(key);
    const previous =
      stored.status === 'ready' && stored.value?.version === 3
        ? stored.value
        : null;
    this.adoptStoredStaleMarks(previous);
    // A stale mark ends only with a grant signed at or after the marking
    // counter value.
    for (const grant of acceptedGrants) {
      const markedAt = this.staleKeys.get(grant.key);
      if (markedAt !== undefined && grant.permissionGraphVersion >= markedAt) {
        this.staleKeys.delete(grant.key);
      }
    }
    const pointer: StoredPermissionPointer = {
      version: 3,
      tenantId,
      confirmedPermissionGraphVersion: Math.max(
        previous?.confirmedPermissionGraphVersion ?? 0,
        this.verifiedPermissionGraphVersion
      ),
      partitions: [...new Set([...(previous?.partitions ?? []), ...partitions])]
        .filter(isPartitionIndex)
        .sort((left, right) => left - right),
      staleKeys: Object.fromEntries(this.staleKeys),
      staleBelowVersion: this.staleBelowVersion,
    };
    if (previous && samePointer(previous, pointer)) return 'ready';
    return writePermissionSnapshotVault(key, pointer);
  }

  async hydrateSignedResponse(
    response: PermissionSnapshotResponse,
    scope: PermissionSnapshotScope
  ): Promise<PermissionSnapshotPayload | null> {
    const outcome = await this.applySignedResponse(response, scope, {
      origin: 'online',
    });
    return outcome.status === 'accepted' ? outcome.payload : null;
  }

  /**
   * Verifies one envelope and merges its grants. An envelope whose grants do
   * not all belong to its partition (or to the expected partition) is
   * rejected as a whole; other envelopes are unaffected. With `collect` its
   * grants are queued for the caller's single merge; without it they are
   * merged right away.
   */
  private async applySignedResponse(
    response: PermissionSnapshotResponse,
    scope: PermissionSnapshotScope,
    {
      origin,
      expectedPartitionIndex,
      collect,
    }: {
      /**
       * `vault` restores stored envelopes; stale ones load as stale. An
       * online base must not be older than the confirmed counter, since it
       * replaces the partition's newer overlays.
       */
      origin: 'online' | 'online-base' | 'vault';
      expectedPartitionIndex?: number;
      collect?: GrantMerge[];
    }
  ): Promise<SignedResponseOutcome> {
    const verification =
      await verifySignedLocalAuthorityEnvelope<PermissionSnapshotContractPayload>(
        response.envelope,
        {
          expectedPurpose: 'permission-grant',
        }
      );
    const signatureVerified =
      verification.status === 'valid' || verification.status === 'expired';
    if (!signatureVerified || !verification.payload) {
      this.reportRejectedSnapshot(`verification:${verification.status}`);
      return { status: 'rejected' };
    }
    const payload = verification.payload;
    // The same key and kid also sign app releases: reject other subjects
    // before any field of the permission payload shape is read.
    if (payload.subjectType !== 'workspace-permission-snapshot') {
      this.reportRejectedSnapshot('contract:subject-type');
      return { status: 'rejected' };
    }
    const contractFailures = [
      payload.workspaceId !== scope.workspaceId ? 'workspace' : null,
      payload.userId !== scope.userId ? 'user' : null,
      scope.tenantId && payload.tenantId !== scope.tenantId ? 'tenant' : null,
      payload.serverId !== scope.serverId ? 'server' : null,
      payload.serverBaseUrlHash !== scope.serverBaseUrlHash
        ? 'server-base-url'
        : null,
      payload.clientInstallId !== scope.clientInstallId
        ? 'client-install'
        : null,
      payload.visibilityMode !== scope.visibilityMode
        ? 'visibility-mode'
        : null,
      payload.denyPrecedence !== true ? 'deny-precedence' : null,
      payload.actionTtlClass !== 'read-view-48h' ? 'action-ttl' : null,
      payload.offlineAccessTtlMs !== undefined &&
      !isValidOfflineAccessTtlMs(payload.offlineAccessTtlMs)
        ? 'offline-access-ttl'
        : null,
      !payload.resources.every(isSignedPermissionSnapshotGrant)
        ? 'resources'
        : null,
      !isPartitionIndex(payload.partitionIndex) ||
      (expectedPartitionIndex !== undefined &&
        payload.partitionIndex !== expectedPartitionIndex) ||
      !payload.resources.every(
        grant =>
          permissionSnapshotPartitionIndex(
            grant.resourceType,
            grant.resourceId
          ) === payload.partitionIndex
      )
        ? 'partition-mismatch'
        : null,
    ].filter((failure): failure is string => failure !== null);
    if (contractFailures.length > 0) {
      this.reportRejectedSnapshot('contract', {
        contractFailures,
      });
      return { status: 'rejected' };
    }
    if (verification.status === 'expired') {
      // Authentic but outdated: keep the resources locked and remember why,
      // so the UI can explain that reconnecting renews them.
      this.markExpired(
        payload.resources
          .filter(grant => grant.allowedActions.length > 0)
          .map(grant => ({
            key: grantKey(grant.resourceType, grant.resourceId),
            resourceType: grant.resourceType,
            source: 'signed' as const,
          }))
      );
      return { status: 'expired' };
    }
    const oldestAccepted =
      origin === 'online'
        ? this.staleBelowVersion
        : origin === 'online-base'
          ? Math.max(
              this.staleBelowVersion,
              this.verifiedPermissionGraphVersion
            )
          : 0;
    if (payload.permissionGraphVersion < oldestAccepted) {
      this.reportRejectedSnapshot('permission-graph-stale', {
        staleBelowVersion: oldestAccepted,
        permissionGraphVersion: payload.permissionGraphVersion,
      });
      return { status: 'rejected' };
    }
    if (payload.offlineAccessTtlMs !== undefined) {
      this.offlineAccessTtlMs = payload.offlineAccessTtlMs;
    }
    // A snapshot ahead of every event this store accounted for means an
    // event is missing or still in flight: every older grant goes stale.
    if (
      origin !== 'vault' &&
      this.verifiedPermissionGraphVersion > 0 &&
      payload.permissionGraphVersion > this.verifiedPermissionGraphVersion
    ) {
      this.markAllStale(payload.permissionGraphVersion);
    } else {
      this.confirmPermissionGraphVersion(payload.permissionGraphVersion);
    }
    this.verifiedTenantId = payload.tenantId;
    const grants = payload.resources.map(materializeSignedGrant);
    const merge: GrantMerge = {
      grants,
      authority: {
        issuedAt: Date.parse(payload.issuedAt),
        expiresAt: Date.parse(payload.expiresAt),
        permissionGraphVersion: payload.permissionGraphVersion,
        scopeFingerprint: permissionScopeFingerprint(scope),
        source: 'signed',
      },
    };
    if (collect) collect.push(merge);
    else this.mergeGrantBatches([merge]);
    let pendingOwnersChanged = false;
    for (const grant of grants) {
      pendingOwnersChanged =
        this.pendingLocalOwners.delete(
          grantKey(grant.resourceType, grant.resourceId)
        ) || pendingOwnersChanged;
    }
    if (pendingOwnersChanged) {
      await this.queuePendingLocalOwnerPersist(scope);
    }
    return { status: 'accepted', payload };
  }

  applyPendingLocalOwner(docId: string, localMutationId: string): void {
    this.applyPendingLocalResourceOwner('doc', docId, localMutationId);
  }

  applyPendingLocalResourceOwner(
    resourceType: 'doc' | 'folder',
    resourceId: string,
    localMutationId: string
  ): void {
    const scope = this.currentScope();
    if (!scope) {
      return;
    }
    const key = grantKey(resourceType, resourceId);
    const createdAt = Date.now();
    const expiresAt = createdAt + this.offlineAccessTtlMs;
    this.pendingLocalOwners.set(key, {
      resourceId,
      resourceType,
      localMutationId,
      createdAt,
      expiresAt,
    });
    this.mergeGrants(
      [
        {
          resourceId,
          resourceType,
          permissions:
            resourceType === 'doc'
              ? ownerDocPermissions()
              : ownerFolderPermissions(),
          permissionSource: 'pending-local-owner',
          localMutationId,
          trashed: false,
          updatedAt: null,
        },
      ],
      {
        issuedAt: createdAt,
        expiresAt,
        permissionGraphVersion: Number.MAX_SAFE_INTEGER,
        scopeFingerprint: permissionScopeFingerprint(scope),
        source: 'pending-local-owner',
      }
    );
    void this.queuePendingLocalOwnerPersist(scope);
  }

  async rejectPendingLocalOwner(
    docId: string,
    expectedLocalMutationId?: string
  ): Promise<boolean> {
    return this.rejectPendingLocalResourceOwner(
      'doc',
      docId,
      expectedLocalMutationId
    );
  }

  async rejectPendingLocalResourceOwner(
    resourceType: 'doc' | 'folder',
    resourceId: string,
    expectedLocalMutationId?: string
  ): Promise<boolean> {
    const scope = this.currentScope();
    const key = grantKey(resourceType, resourceId);
    const pending = this.pendingLocalOwners.get(key);
    if (
      !scope ||
      !pending ||
      (expectedLocalMutationId !== undefined &&
        pending.localMutationId !== expectedLocalMutationId)
    ) {
      return false;
    }

    this.pendingLocalOwners.delete(key);
    const grant = this.grants.get(key);
    if (
      grant?.permissionSource === 'pending-local-owner' &&
      grant.localMutationId === pending.localMutationId
    ) {
      this.removeGrant(key);
    }
    await this.queuePendingLocalOwnerPersist(scope);
    return true;
  }

  private mergeGrants(
    grants: PermissionSnapshotGrant[],
    authority: GrantAuthority
  ): void {
    this.mergeGrantBatches([{ grants, authority }]);
  }

  /**
   * Merges the grants of a whole pass (all pages of a load, all envelopes of
   * one server answer) with one copy of the grant map and one emission, so
   * the work grows with the grants, not with pages times grants.
   */
  private mergeGrantBatches(merges: GrantMerge[]): void {
    if (merges.every(merge => merge.grants.length === 0)) {
      return;
    }
    let expired: Map<string, ExpiredGrant> | null = null;
    for (const { grants, authority } of merges) {
      for (const grant of grants) {
        const key = grantKey(grant.resourceType, grant.resourceId);
        const current = this.grantAuthority.get(key);
        // Among signed grants the one with the highest (graph version, issue
        // time) wins, whatever order the envelopes arrive in.
        if (
          authority.source === 'signed' &&
          current?.source === 'signed' &&
          compareSignedAuthority(current, authority) > 0
        ) {
          continue;
        }
        this.grants.set(key, grant);
        this.grantAuthority.set(key, authority);
        if (this.expiredGrants$.value.has(key)) {
          expired ??= new Map(this.expiredGrants$.value);
          expired.delete(key);
        }
      }
    }
    this.bumpGrantsVersion();
    if (expired) this.expiredGrants$.next(expired);
    this.scheduleExpiryCheck();
  }

  /**
   * Locks every grant whose offline window ended. Runs from a timer so open
   * documents lock at the expiry instant, not only on their next access.
   */
  expireDueGrants(now = Date.now()): void {
    const due: ExpiredGrantEntry[] = [];
    for (const [key, authority] of this.grantAuthority) {
      if (authority.expiresAt > now) continue;
      const grant = this.grants.get(key);
      if (grant && grantAllowsAnything(grant)) {
        due.push({
          key,
          resourceType: grant.resourceType,
          source: authority.source,
        });
      }
    }
    const dueKeys = [...this.grantAuthority]
      .filter(([, authority]) => authority.expiresAt <= now)
      .map(([key]) => key);
    if (dueKeys.length > 0) {
      for (const key of dueKeys) {
        this.grants.delete(key);
        this.grantAuthority.delete(key);
      }
      this.bumpGrantsVersion();
    }
    this.markExpired(due);
    this.scheduleExpiryCheck();
  }

  private markExpired(entries: ExpiredGrantEntry[]): void {
    const now = Date.now();
    const fresh = entries.filter(entry => {
      const authority = this.grantAuthority.get(entry.key);
      return !authority || authority.expiresAt <= now;
    });
    if (fresh.length === 0) return;
    const next = new Map(this.expiredGrants$.value);
    for (const entry of fresh) {
      next.set(entry.key, {
        resourceType: entry.resourceType,
        source: entry.source,
      });
    }
    this.expiredGrants$.next(next);
  }

  private scheduleExpiryCheck(): void {
    this.clearExpiryTimer();
    let next: number | null = null;
    for (const authority of this.grantAuthority.values()) {
      if (next === null || authority.expiresAt < next) {
        next = authority.expiresAt;
      }
    }
    if (next === null) return;
    const delay = Math.min(Math.max(next - Date.now(), 0), MAX_TIMER_DELAY_MS);
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = null;
      this.expireDueGrants();
    }, delay);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
  }

  private async hydrateLocalOnce(): Promise<void> {
    const startedAt = performance.now();
    let outcome = 'missing-scope';
    let partitionCount = 0;
    let validPartitions = 0;
    recordBootEvent('permission-cache-hydration-started');
    const scope = this.currentScope();
    if (!scope) {
      recordBootEvent('permission-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        outcome,
        partitionCount,
        validPartitions,
      });
      return;
    }
    outcome = 'error';
    try {
      const pendingStartedAt = performance.now();
      await this.hydratePendingLocalOwners(scope);
      recordBootEvent('permission-cache-pending-owners-ready', {
        durationMs: performance.now() - pendingStartedAt,
      });

      const pointerStartedAt = performance.now();
      const pointer =
        await readPermissionSnapshotVault<StoredPermissionPointer>(
          this.scopePointerKey(scope)
        );
      partitionCount =
        pointer.status === 'ready' && pointer.value?.version === 3
          ? pointer.value.partitions.length
          : 0;
      recordBootEvent('permission-cache-pointer-read', {
        durationMs: performance.now() - pointerStartedAt,
        partitionCount,
        status: pointer.status,
      });
      if (
        pointer.status !== 'ready' ||
        !pointer.value ||
        pointer.value.version !== 3
      ) {
        outcome =
          pointer.status === 'ready' ? 'missing-pointer' : pointer.status;
        if (pointer.status !== 'ready') this.vaultStatus$.next(pointer.status);
        else this.hydratedScopeKey = this.scopePointerKey(scope);
        return;
      }
      const scopedStorage = {
        ...scope,
        tenantId: pointer.value.tenantId,
      };
      this.verifiedPermissionGraphVersion = Math.max(
        this.verifiedPermissionGraphVersion,
        pointer.value.confirmedPermissionGraphVersion
      );
      this.adoptStoredStaleMarks(pointer.value);
      let vaultReadTotalMs = 0;
      let vaultReadMaxMs = 0;
      let verifyTotalMs = 0;
      let verifyMaxMs = 0;
      const partitionsStartedAt = performance.now();
      const merges: GrantMerge[] = [];
      let unreadablePartition = false;
      const partitionResults = await mapWithConcurrency(
        pointer.value.partitions,
        LOCAL_HYDRATION_CONCURRENCY,
        async partitionIndex => {
          const key = this.partitionKey(scopedStorage, partitionIndex);
          const readStartedAt = performance.now();
          const stored =
            await readPermissionSnapshotVault<StoredPermissionPartition>(key);
          const readDurationMs = performance.now() - readStartedAt;
          vaultReadTotalMs += readDurationMs;
          vaultReadMaxMs = Math.max(vaultReadMaxMs, readDurationMs);
          if (stored.status !== 'ready') {
            this.vaultStatus$.next(stored.status);
            // Locked or briefly unavailable: worth another read. Corrupt or
            // unsupported stays so; reading it again only costs time.
            if (!PERMANENT_VAULT_FAILURES.has(stored.status)) {
              unreadablePartition = true;
            }
            return false;
          }
          if (
            !stored.value ||
            stored.value.version !== 3 ||
            stored.value.partitionIndex !== partitionIndex
          ) {
            return false;
          }
          let restored = false;
          // Memory takes the partition's grants only if the vault keeps it.
          const partitionMerges: GrantMerge[] = [];
          for (const entry of [
            ...stored.value.base,
            ...stored.value.overlays,
          ]) {
            const verifyStartedAt = performance.now();
            const outcome = await this.applySignedResponse(
              { envelope: entry.envelope as PermissionSnapshotEnvelope },
              { ...scope, tenantId: scopedStorage.tenantId },
              {
                origin: 'vault',
                expectedPartitionIndex: partitionIndex,
                collect: partitionMerges,
              }
            );
            const verifyDurationMs = performance.now() - verifyStartedAt;
            verifyTotalMs += verifyDurationMs;
            verifyMaxMs = Math.max(verifyMaxMs, verifyDurationMs);
            // Expired envelopes stay in the vault: they prove which resources
            // need renewal and are replaced by the next online response.
            if (outcome.status === 'expired') continue;
            if (outcome.status !== 'accepted') {
              await deletePermissionSnapshotVault(key);
              return false;
            }
            restored = true;
          }
          merges.push(...partitionMerges);
          return restored;
        }
      );
      this.mergeGrantBatches(merges);
      validPartitions = partitionResults.filter(Boolean).length;
      outcome = validPartitions > 0 ? 'restored' : 'empty';
      if (!unreadablePartition) {
        this.hydratedScopeKey = this.scopePointerKey(scope);
      }
      recordBootEvent('permission-cache-partitions-ready', {
        durationMs: performance.now() - partitionsStartedAt,
        partitionCount,
        validPartitions,
        vaultReadMaxMs,
        vaultReadTotalMs,
        verifyMaxMs,
        verifyTotalMs,
      });
      // A pointer whose partitions were all read and none held a valid grant
      // is dead. An unreadable partition (locked vault) proves nothing yet.
      if (
        !unreadablePartition &&
        validPartitions === 0 &&
        pointer.value.partitions.length > 0
      ) {
        await deletePermissionSnapshotVault(this.scopePointerKey(scope));
      }
    } finally {
      recordBootEvent('permission-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        outcome,
        partitionCount,
        validPartitions,
      });
    }
  }

  /** The local authority scope of this person, workspace and server. */
  currentScope(): PermissionSnapshotScope | null {
    const server = this.workspaceServerService.server;
    const userId = server
      ? resolveWorkspaceLocalAuthorityAccountId({
          liveAccountId: server.confirmedAccountId,
          cachedAccountId: server.cachedAccountId,
          scopeReader: this.globalState,
          serverBaseUrl: server.baseUrl,
          serverId: server.id,
          workspaceId: this.workspaceService.workspace.id,
        })
      : null;
    if (!userId) {
      return null;
    }
    const serverBaseUrlHash = stableLocalAuthorityScopeHash(
      canonicalServerOrigin(server?.baseUrl ?? 'http://localhost:8080')
    );
    return {
      workspaceId: this.workspaceService.workspace.id,
      userId,
      serverId: `streamlain:${serverBaseUrlHash}`,
      serverBaseUrlHash,
      clientInstallId: getLocalAuthorityClientInstallId(),
      visibilityMode: 'user_effective',
    };
  }

  private partitionKey(
    scope: PermissionSnapshotScope,
    partitionIndex: number
  ): string {
    const server = this.workspaceServerService.server;
    return (
      PERMISSION_SNAPSHOT_KEY_PREFIX.partition +
      [
        server?.id ?? 'local',
        server?.baseUrl ?? 'local',
        scope.tenantId ?? 'tenant-pending',
        scope.workspaceId,
        scope.userId,
        scope.clientInstallId,
        scope.visibilityMode,
        scope.serverBaseUrlHash,
        partitionIndex,
      ].join(':')
    );
  }

  private isMarkedStale(key: string, permissionGraphVersion: number): boolean {
    if (permissionGraphVersion < this.staleBelowVersion) return true;
    const markedAt = this.staleKeys.get(key);
    return markedAt !== undefined && permissionGraphVersion < markedAt;
  }

  private scopePointerKey(scope: PermissionSnapshotScope): string {
    return PERMISSION_SNAPSHOT_KEY_PREFIX.pointer + scopeKeyParts(scope);
  }

  private pendingLocalOwnerKey(scope: PermissionSnapshotScope): string {
    return PERMISSION_SNAPSHOT_KEY_PREFIX.pendingOwner + scopeKeyParts(scope);
  }

  private async hydratePendingLocalOwners(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const stored =
      await readPermissionSnapshotVault<StoredPendingLocalOwnerGrants>(
        this.pendingLocalOwnerKey(scope)
      );
    if (stored.status !== 'ready' || stored.value?.version !== 3) {
      return;
    }

    const now = Date.now();
    const grants: PermissionSnapshotGrant[] = [];
    const expired: ExpiredGrantEntry[] = [];
    for (const pending of stored.value.grants) {
      if (!pending.resourceId || !pending.localMutationId) {
        continue;
      }
      const key = grantKey(pending.resourceType, pending.resourceId);
      this.pendingLocalOwners.set(key, pending);
      if (pending.expiresAt <= now) {
        // The local creator grant ended with the offline window; keep the
        // record so the server confirmation can still reconcile it.
        expired.push({
          key,
          resourceType: pending.resourceType,
          source: 'pending-local-owner',
        });
        continue;
      }
      grants.push({
        resourceId: pending.resourceId,
        resourceType: pending.resourceType,
        permissions:
          pending.resourceType === 'doc'
            ? ownerDocPermissions()
            : ownerFolderPermissions(),
        permissionSource: 'pending-local-owner',
        localMutationId: pending.localMutationId,
        trashed: false,
        updatedAt: null,
      });
    }
    const merges: GrantMerge[] = [];
    for (const grant of grants) {
      const pending = this.pendingLocalOwners.get(
        grantKey(grant.resourceType, grant.resourceId)
      );
      if (!pending) continue;
      merges.push({
        grants: [grant],
        authority: {
          issuedAt: pending.createdAt,
          expiresAt: pending.expiresAt,
          permissionGraphVersion: Number.MAX_SAFE_INTEGER,
          scopeFingerprint: permissionScopeFingerprint(scope),
          source: 'pending-local-owner',
        },
      });
    }
    this.mergeGrantBatches(merges);
    this.markExpired(expired);
    await this.queuePendingLocalOwnerPersist(scope);
  }

  private getActiveGrant(
    resourceType: 'doc' | 'folder',
    resourceId: string
  ): PermissionSnapshotGrant | null {
    const key = grantKey(resourceType, resourceId);
    const grant = this.grants.get(key);
    const authority = this.grantAuthority.get(key);
    const scope = this.currentScope();
    if (grant && authority && authority.expiresAt <= Date.now()) {
      this.expireDueGrants();
      return null;
    }
    const markedStale =
      authority?.source === 'signed' &&
      this.isMarkedStale(key, authority.permissionGraphVersion);
    if (
      !grant ||
      !authority ||
      !scope ||
      authority.scopeFingerprint !== permissionScopeFingerprint(scope) ||
      markedStale
    ) {
      if (scope && authority?.source === 'pending-local-owner') {
        this.pendingLocalOwners.delete(key);
        void this.queuePendingLocalOwnerPersist(scope);
      }
      // A stale mark already told the watchers (every marking bumps);
      // other causes (scope change) are news to them.
      this.removeGrant(key, { notify: !markedStale });
      return null;
    }
    return grant;
  }

  private removeGrant(key: string, { notify = true } = {}): void {
    if (!this.grants.has(key) && !this.grantAuthority.has(key)) return;
    this.grants.delete(key);
    this.grantAuthority.delete(key);
    if (notify) this.bumpGrantsVersion();
  }

  private bumpGrantsVersion(): void {
    this.grantsVersion$.next(this.grantsVersion$.value + 1);
  }

  private async persistPendingLocalOwners(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const key = this.pendingLocalOwnerKey(scope);
    if (this.pendingLocalOwners.size === 0) {
      await deletePermissionSnapshotVault(key);
      return;
    }
    await writePermissionSnapshotVault(key, {
      grants: [...this.pendingLocalOwners.values()],
      version: 3,
    } satisfies StoredPendingLocalOwnerGrants);
  }

  private queuePendingLocalOwnerPersist(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const next = this.pendingLocalOwnerWrite.then(() =>
      this.persistPendingLocalOwners(scope)
    );
    this.pendingLocalOwnerWrite = next.catch(error => {
      console.error('Failed to persist pending local ownership:', error);
    });
    return next;
  }

  private reportRejectedSnapshot(
    reason: string,
    details?: Record<string, unknown>
  ): void {
    if (reason === 'permission-graph-stale') {
      const staleSnapshotKey = `${String(
        details?.staleBelowVersion ?? 'unknown'
      )}:${String(details?.permissionGraphVersion ?? 'unknown')}`;
      if (staleSnapshotKey === this.lastReportedStaleSnapshotKey) {
        return;
      }
      this.lastReportedStaleSnapshotKey = staleSnapshotKey;
      console.debug('Discarded stale permission snapshot', {
        reason,
        ...details,
      });
      return;
    }
    console.error('Rejected cached permission snapshot', {
      reason,
      ...details,
    });
  }
}

interface GrantAuthority {
  issuedAt: number;
  expiresAt: number;
  permissionGraphVersion: number;
  scopeFingerprint: string;
  source: 'signed' | 'pending-local-owner';
}

/** Grants of one envelope with the authority they were issued under. */
interface GrantMerge {
  grants: PermissionSnapshotGrant[];
  authority: GrantAuthority;
}

interface PendingBaseChunks {
  chunkCount: number;
  chunks: Map<number, StoredSignedEnvelope[]>;
}

interface AcceptedGrantVersion {
  key: string;
  permissionGraphVersion: number;
}

type VaultWriteStatus = Awaited<
  ReturnType<typeof writePermissionSnapshotVault>
>;

interface StoredPendingLocalOwnerGrants {
  grants: PendingLocalOwner[];
  version: 3;
}

interface PendingLocalOwner {
  resourceId: string;
  resourceType: 'doc' | 'folder';
  localMutationId: string;
  createdAt: number;
  /** createdAt + workspace offline-access window at creation time. */
  expiresAt: number;
}

export interface ExpiredGrant {
  resourceType: 'doc' | 'folder';
  source: GrantAuthority['source'];
}

interface ExpiredGrantEntry extends ExpiredGrant {
  key: string;
}

type SignedResponseOutcome =
  | { status: 'accepted'; payload: PermissionSnapshotPayload }
  | { status: 'expired' }
  | { status: 'rejected' };

export type LocalPermissionVaultStatus =
  | 'ready'
  | 'unavailable'
  | 'locked'
  | 'corrupt'
  | 'unsupported'
  | 'degraded';

export type LocalPermissionGrantState =
  | 'valid'
  | 'stale'
  | 'unknown'
  | 'invalid'
  | 'expired'
  | 'locked'
  | 'degraded'
  | 'pending-local';

/** setTimeout overflows above 2^31-1 ms (~24.8 days); re-arm in chunks. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const LOCAL_HYDRATION_CONCURRENCY = 8;

function isPartitionIndex(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < PERMISSION_SNAPSHOT_PARTITION_COUNT
  );
}

function compareSignedAuthority(
  left: GrantAuthority,
  right: GrantAuthority
): number {
  return (
    left.permissionGraphVersion - right.permissionGraphVersion ||
    left.issuedAt - right.issuedAt
  );
}

interface StoredEnvelopeRank {
  permissionGraphVersion: number;
  issuedAt: number;
  keys: string[];
}

/** Reads the ranking fields of an envelope this store verified and stored. */
function storedEnvelopeRank(stored: StoredSignedEnvelope): StoredEnvelopeRank {
  const payload = (
    stored.envelope as { payload?: Partial<PermissionSnapshotPayload> }
  ).payload;
  return {
    permissionGraphVersion: payload?.permissionGraphVersion ?? 0,
    issuedAt: Date.parse(payload?.issuedAt ?? '') || 0,
    keys: (payload?.resources ?? []).map(grant =>
      grantKey(grant.resourceType, grant.resourceId)
    ),
  };
}

/**
 * Drops every stored envelope (base chunk or overlay) whose grants are all
 * superseded by a newer envelope of the partition: higher graph version, then
 * later issue time, then later position (overlays after base, in arrival
 * order). Reloading the same list page thus replaces its old overlay.
 */
function removeCoveredEnvelopes(record: StoredPermissionPartition): void {
  const entries = [...record.base, ...record.overlays].map(
    (stored, position) => ({ stored, position, ...storedEnvelopeRank(stored) })
  );
  type Entry = (typeof entries)[number];
  const isNewer = (entry: Entry, than: Entry) =>
    (entry.permissionGraphVersion - than.permissionGraphVersion ||
      entry.issuedAt - than.issuedAt ||
      entry.position - than.position) > 0;
  const newestByKey = new Map<string, Entry>();
  for (const entry of entries) {
    for (const key of entry.keys) {
      const newest = newestByKey.get(key);
      if (!newest || isNewer(entry, newest)) newestByKey.set(key, entry);
    }
  }
  const kept = new Set(
    entries
      .filter(entry => entry.keys.some(key => newestByKey.get(key) === entry))
      .map(entry => entry.stored)
  );
  record.base = record.base.filter(stored => kept.has(stored));
  record.overlays = record.overlays.filter(stored => kept.has(stored));
}

/** Same grant content; the signing times are compared by the caller. */
function sameGrant(
  held: PermissionSnapshotGrant | undefined,
  next: PermissionSnapshotGrant
): boolean {
  return (
    held !== undefined &&
    held.role === next.role &&
    held.permissionSource === next.permissionSource &&
    held.trashed === next.trashed &&
    held.updatedAt === next.updatedAt &&
    allowedActionsKey(held) === allowedActionsKey(next)
  );
}

function allowedActionsKey(grant: PermissionSnapshotGrant): string {
  return Object.entries(grant.permissions)
    .filter(([, allowed]) => allowed)
    .map(([action]) => action)
    .sort()
    .join(',');
}

function samePointer(
  left: StoredPermissionPointer,
  right: StoredPermissionPointer
): boolean {
  return (
    left.tenantId === right.tenantId &&
    left.confirmedPermissionGraphVersion ===
      right.confirmedPermissionGraphVersion &&
    left.staleBelowVersion === right.staleBelowVersion &&
    left.partitions.join(',') === right.partitions.join(',') &&
    staleKeysKey(left.staleKeys) === staleKeysKey(right.staleKeys)
  );
}

function staleKeysKey(staleKeys: StoredPermissionPointer['staleKeys']): string {
  return Object.entries(staleKeys)
    .map(([key, markedAt]) => `${key}=${markedAt}`)
    .sort()
    .join(',');
}

function scopeKeyParts(scope: PermissionSnapshotScope): string {
  return [
    scope.serverId,
    scope.serverBaseUrlHash,
    scope.workspaceId,
    scope.userId,
    scope.clientInstallId,
    scope.visibilityMode,
  ].join(':');
}

async function mapWithConcurrency<T, R>(
  values: readonly T[],
  concurrency: number,
  mapper: (value: T) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(values.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, values.length) },
    async () => {
      while (nextIndex < values.length) {
        const index = nextIndex++;
        results[index] = await mapper(values[index]);
      }
    }
  );
  await Promise.all(workers);
  return results;
}

function ownerDocPermissions(): Record<string, boolean> {
  return {
    Doc_Comments_Create: true,
    Doc_Comments_Delete: true,
    Doc_Comments_Read: true,
    Doc_Comments_Resolve: true,
    Doc_Copy: true,
    Doc_Delete: true,
    Doc_Duplicate: true,
    Doc_Properties_Read: true,
    Doc_Properties_Update: true,
    Doc_Publish: true,
    Doc_Read: true,
    Doc_Restore: true,
    Doc_TransferOwner: true,
    Doc_Trash: true,
    Doc_Update: true,
    Doc_Users_Manage: true,
    Doc_Users_Read: true,
  };
}

function ownerFolderPermissions(): Record<string, boolean> {
  return {
    Folder_Read: true,
    Folder_Update: true,
    Folder_Delete: true,
    Folder_Trash: true,
    Folder_Restore: true,
    Folder_Move: true,
    Folder_AddDoc: true,
    Folder_RemoveDoc: true,
    Folder_CreateSubfolder: true,
    Folder_Users_Manage: true,
    Folder_Users_Read: true,
  };
}

function grantAllowsAnything(
  grant: PermissionSnapshotGrant | undefined
): boolean {
  return !!grant && Object.values(grant.permissions).some(allowed => allowed);
}

function grantKey(resourceType: 'doc' | 'folder', resourceId: string): string {
  return `${resourceType}:${resourceId}`;
}

function permissionScopeFingerprint(scope: PermissionSnapshotScope): string {
  return [
    scope.workspaceId,
    scope.userId,
    scope.serverId,
    scope.serverBaseUrlHash,
    scope.clientInstallId,
    scope.visibilityMode,
  ].join(':');
}

function canonicalServerOrigin(value: string): string {
  try {
    return new URL(value).origin.toLowerCase();
  } catch {
    return value.trim().toLowerCase();
  }
}

function materializeSignedGrant(
  grant: SignedPermissionSnapshotGrant
): PermissionSnapshotGrant {
  return {
    resourceId: grant.resourceId,
    resourceType: grant.resourceType,
    permissions: Object.fromEntries(
      grant.allowedActions.map(action => [action, true])
    ),
    role: grant.role,
    permissionSource: grant.permissionSource,
    trashed: grant.trashed,
    updatedAt: grant.updatedAt,
  };
}
